import { faTrianglePersonDigging } from '@fortawesome/pro-light-svg-icons'
import {
  ResidentialApartmentPropertyDetails,
  ResidentialHousePropertyDetails,
  ResidentialType,
} from '@unreserved-frontend-v2/api/generated/graphql/types'
import { DetailsTableRow } from '@unreserved-frontend-v2/ui/details-table/models'
import { emptyDefault } from '@unreserved-frontend-v2/utils/empty-default'
import useTranslation from 'next-translate/useTranslation'
import { useMemo } from 'react'
import { yearBuilt } from '../../../../utils/formatters/year-built'

export const useBuildRow = (
  details?: ResidentialApartmentPropertyDetails | ResidentialHousePropertyDetails | null
): DetailsTableRow | undefined => {
  const { t } = useTranslation()
  return useMemo(() => {
    const yearBuiltFormatted = details ? yearBuilt(details) : null
    const yearBuiltValue = emptyDefault(yearBuiltFormatted)

    if (
      [
        !yearBuiltFormatted,
        !details?.builderModel,
        !(details as ResidentialHousePropertyDetails).basementFinishRaw,
        !details?.exteriorRaw,
        !(details as ResidentialHousePropertyDetails).foundationRaw,
        !details?.builderName,
        !(details as ResidentialApartmentPropertyDetails).stories,
        !(details as ResidentialHousePropertyDetails).levels,
        !details?.flooringRaw,
        !(details as ResidentialHousePropertyDetails).roofRaw,
      ].every(Boolean)
    ) {
      return
    }

    const isHouse = details?.residentialType === ResidentialType.House

    let foundationValue = null
    let basementFinishValue = null

    if (isHouse) {
      const house = details as ResidentialHousePropertyDetails
      foundationValue = emptyDefault(house.foundationRaw)
      basementFinishValue = emptyDefault(house.basementFinishRaw)
    }

    return {
      icon: faTrianglePersonDigging,
      title: t('listings:details-pages.build'),
      cells: [
        {
          id: 'build',
          data: [
            {
              id: 'data-year-built',
              label: t('listings:details-pages.year-built'),
              value: emptyDefault(yearBuiltValue),
            },
            {
              id: 'data-model-name',
              label: t('listings:details-pages.model-name'),
              value: emptyDefault(details?.builderModel),
            },
            ...(isHouse
              ? [
                  {
                    id: 'data-basement',
                    label: t('listings:details-pages.basement'),
                    value: basementFinishValue,
                  },
                ]
              : []),
            {
              id: 'data-exterior',
              label: t('listings:details-pages.exterior'),
              value: emptyDefault(details?.exteriorRaw),
            },
            ...(isHouse
              ? [
                  {
                    id: 'data-foundation',
                    label: t('listings:details-pages.foundation'),
                    value: emptyDefault(foundationValue),
                  },
                ]
              : []),
          ],
        },
        {
          id: 'property-type',
          data: [
            {
              id: 'data-builder-name',
              label: t('listings:details-pages.builder-name'),
              value: emptyDefault(details?.builderName),
            },
            ...(isHouse
              ? [
                  {
                    id: 'data-stories',
                    label: t('listings:details-pages.stories'),
                    value: emptyDefault(details.stories),
                  },
                ]
              : [
                  {
                    id: 'data-levels',
                    label: t('listings:details-pages.levels'),
                    value: emptyDefault(details?.levels),
                  },
                ]),
            {
              id: 'data-flooring',
              label: t('listings:details-pages.flooring'),
              value: emptyDefault(details?.flooringRaw),
            },
            ...(isHouse
              ? [
                  {
                    id: 'data-roof',
                    label: t('listings:details-pages.roof'),
                    value: emptyDefault(details?.roofRaw),
                  },
                ]
              : []),
          ],
        },
      ],
    }
  }, [details, t])
}
